import {
  LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import {
  getPickupProgress,
  RetailProductOrderActions,
  RetailProductPickupSection,
} from "modules/retailProducts";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async (packageId: string) => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocations = queryClient.fetchQuery(
    ["/platform/locations"],
    () => get("/platform/locations", { page: 1 })
  );

  // Get all earned products and find the one with matching packageId
  const earnedProducts = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/earned"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/earned", {
        platformId: platformData.id,
      });
    }
  );

  return waitObj({
    platform,
    platformLocations,
    earnedProducts,
    packageId,
  });
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletEarnedProductDetailsLoader: LoaderFunction = async ({
  params,
}) => {
  const { packageId } = params;
  if (!packageId) {
    throw new Error("Package ID is required");
  }
  return await load(packageId);
};

const WalletEarnedProductDetailsPage = () => {
  const { earnedProducts, platformLocations, packageId } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get("type");

  // Find the specific earned product
  const earnedProduct = earnedProducts.find(
    (product) => product.packageId === packageId
  );

  if (!earnedProduct) {
    return (
      <div className="p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-semibold text-gray-900">
            Product Not Found
          </h1>
          <p className="mb-4 text-gray-500">
            The earned product you&apos;re looking for could not be found.
          </p>
          <Button onClick={() => navigate("/wallet/products")} style="primary">
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const locations =
    (platformLocations.items as Models<"PlatformLocationModel1">[]) || [];
  // For earned products, we don't have locationId at the line item level
  // We'll use the first available location or a default
  const location = locations.length > 0 ? locations[0] : null;

  const pickupProgress = getPickupProgress([earnedProduct]);

  return (
    <div className="flex h-full flex-col space-y-4 bg-two">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate("/wallet/products");
        }}
      >
        <h1 className="text-title">Products</h1>
      </TopNav>

      {/* Pickup Section */}
      <div className="px-4">
        <div className="rounded-lg bg-white p-4 shadow-subtle">
          <RetailProductPickupSection
            context="product-details"
            pickupItems={[earnedProduct]}
            pickupProgress={pickupProgress}
            locationName={location?.name}
            orderType={orderType || "earned"}
          />
        </div>
      </div>

      <RetailProductOrderActions
        cancelEligibilityExpired={false}
        refundDescription=""
        orderId={earnedProduct.packageId}
        hasPickupItems={true}
        hasShippingItems={false}
        showNeedHelp={false}
      />
    </div>
  );
};

export default WalletEarnedProductDetailsPage;
